import React, { createContext, useContext, useEffect, useState } from "react";
import { wsEndpoint } from "../utils/endpoints";
import { useDispatch, useSelector } from "react-redux";
import { authSelectors } from "../store/auth/selector";
import useWebSockets from "react-use-websocket";
import { useQueryClient } from "@tanstack/react-query";
import { toasts } from "../components/toasts/toasts";
import { useInvalidateQueryKeys } from "../hooks/useInvalidateQueryKeys";
import { DealFirmGroupPresence } from "../types";
import { actions } from "../store/presence/slice";
import { actions as assistantActions } from "../store/assistant/slice";
import { useAuth } from "@clerk/clerk-react";
import {
  DealExportStatus,
  DealThreadMessageContentBlockType,
  DealThreadMessageContentQuoteStatus,
  DealThreadMessageRole,
  DealThreadMessageSource,
  DealThreadMessageStatus,
  FileType,
  SearchDealResultChunkExcel,
  SuggestedAnswerBlockType,
  SuggestedAnswerStatus,
  QuestionQuery,
} from "../graphql/generated";
import { getUnixTime } from "date-fns";
import { useParams } from "react-router-dom";

// Define a type for the WebSocket context value
interface WebSocketContextType {
  sendMessage: (message: string) => void;
  subscribeToTopic: (topic: string) => void;
  lastMessage: MessageEvent<any> | null;
  unsubscribeFromTopic: (topic: string) => void;
}

// Create the context with an initial undefined value
const WebSocketContext = createContext<WebSocketContextType | null>(null);

// Custom hook to use the WebSocket context
export const useWebSocket = () =>
  useContext(WebSocketContext) as WebSocketContextType;

// Props type for the provider
interface WebSocketProviderProps {
  children: React.ReactNode;
}

export enum WebsocketMessageType {
  DataRoomFileError = "data_room_file_error",
  DataRoomFileErrorUpdated = "data_room_file_error_updated",
  DataRoomFileErrorDismissed = "data_room_file_error_dismissed",
  DataRoomFileCreated = "data_room_file_created",
  DataRoomFileUpdated = "data_room_file_updated",
  DataRoomFileVersionCreated = "data_room_file_version_created",
  DataRoomFileVersionUpdated = "data_room_file_version_updated",

  DealThreadMessageCreated = "deal_thread_message_created",
  DealThreadMessageUpdated = "deal_thread_message_updated",

  DdqlImportComplete = "ddql_import_complete",

  DealFirmGroupPresence = "deal_firm_group_presence",

  DealExportUpdated = "deal_export_updated",

  SuggestedAnswerUpdated = "suggested_answer_updated",
}

export function WebSocketProvider(props: WebSocketProviderProps) {
  const account = useSelector(authSelectors.account);
  const { dealId: activeDealId } = useParams<{ dealId: string }>();
  const dispatch = useDispatch();

  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    async function fetchToken() {
      if (!isLoaded || !isSignedIn) {
        setToken(null);
        return;
      }
      const token = await getToken();
      setToken(token);
    }

    fetchToken();
  }, [isLoaded, isSignedIn]);

  const {
    invalidateDealActivities,
    invalidateDataRoomFolders,
    invalidateDataRoomFile,
  } = useInvalidateQueryKeys();

  const queryClient = useQueryClient();

  const { sendJsonMessage, lastMessage } = useWebSockets(
    wsEndpoint(),
    {
      share: true,
      shouldReconnect: () => !!token,
      queryParams: { token: token ?? "" },
      onClose: (e) => console.log("WebSocket closed:", e),
      onOpen: (e) => {
        console.log("WebSocket opened:", e);
        subscribeToDeals();
      },
      onMessage: (e) => handleMessage(e.data),
      onError: (e) => console.error("WebSocket error:", e),
    },
    !!token,
  );

  function subscribeToDeals() {
    if (!account || !account.deals) {
      return;
    }

    for (const d of account.deals) {
      subscribeToTopic(`deal:${d.id}`);
    }
  }

  const sendMessage = (message: any) => {
    sendJsonMessage(message);
  };

  const subscribeToTopic = (topic: string) => {
    sendJsonMessage({ action: "subscribe", topic: topic });
  };

  const unsubscribeFromTopic = (topic: string) => {
    sendJsonMessage({ action: "unsubscribe", topic: topic });
  };

  const handleMessage = (message: string) => {
    try {
      const messageData = JSON.parse(message) as WebsocketMessage;
      switch (messageData.type) {
        case WebsocketMessageType.DataRoomFileError:
          handleDataRoomFileError(messageData.data);
          break;
        case WebsocketMessageType.DataRoomFileErrorUpdated:
          handleDataRoomFileErrorUpdated(messageData.data);
          break;
        case WebsocketMessageType.DataRoomFileCreated:
          handleDataRoomFileCreated(messageData.data);
          break;
        case WebsocketMessageType.DataRoomFileUpdated:
          handleDataRoomFileUpdated(messageData.data);
          break;
        case WebsocketMessageType.DataRoomFileVersionCreated:
          handleDataRoomFileVersionCreated(messageData.data);
          break;
        case WebsocketMessageType.DataRoomFileVersionUpdated:
          handleDataRoomFileVersionUpdated(messageData.data);
          break;
        case WebsocketMessageType.DataRoomFileErrorDismissed:
          handleDataRoomFileErrorDismissed(messageData.data);
          break;
        case WebsocketMessageType.DealThreadMessageCreated:
          handleDealThreadMessageCreated(messageData.data);
          break;
        case WebsocketMessageType.DealThreadMessageUpdated:
          handleDealThreadMessageUpdated(messageData.data);
          break;
        case WebsocketMessageType.DdqlImportComplete:
          handleDdqlImportComplete(messageData.data);
          break;
        case WebsocketMessageType.DealFirmGroupPresence:
          handleDealFirmGroupPresence(messageData.data);
          break;
        case WebsocketMessageType.DealExportUpdated:
          handleDealExportUpdated(messageData.data);
          break;

        case WebsocketMessageType.SuggestedAnswerUpdated:
          handleSuggestedAnswerUpdated(messageData.data);
          break;

        default:
          console.error("Unknown message type:", messageData);
      }
    } catch (e) {
      console.error("Failed to parse message:", e, message);
    }
  };

  function handleDealFirmGroupPresence(message: DealFirmGroupPresenceData) {
    dispatch(actions.updateDealFirmGroupPresence(message.dealFirmGroup));
  }

  function handleDdqlImportComplete(message: DdqlImportCompleteData) {
    queryClient.invalidateQueries({
      queryKey: [
        "DealFirmGroupQuestions",
        {
          id: message.dealFirmGroupID,
        },
      ],
    });

    queryClient.invalidateQueries({
      queryKey: [
        "Ddql",
        {
          id: message.ddqlID,
        },
      ],
    });

    toasts.success("DDQL import complete");
  }

  function handleSuggestedAnswerUpdated(
    message: SuggestedAnswerUpdatedData,
  ) {
    queryClient.setQueriesData<QuestionQuery>(
      { queryKey: ["Question", { id: message.questionID }] },
      (old) => {
        if (!old) return old;
        return {
          ...old,
          question: {
            ...old.question,
            suggestedAnswer: {
              __typename: "SuggestedAnswer" as const,
              id: message.suggestedAnswer.id,
              status: message.suggestedAnswer.status,
              summary: message.suggestedAnswer.summary,
              blocks: message.suggestedAnswer.blocks.map((b) => ({
                __typename: "SuggestedAnswerBlock" as const,
                type: b.type,
                text: b.text ?? null,
                reasoning: b.reasoning ?? null,
                durationMs: b.durationMs ?? null,
                startedAtMs: b.startedAtMs ?? null,
                endedAtMs: b.endedAtMs ?? null,
                toolName: b.toolName ?? null,
                toolArgs: b.toolArgs ?? null,
                toolCallId: b.toolCallId ?? null,
                toolResult: b.toolResult ?? null,
              })),
              relevantFiles: message.suggestedAnswer.relevantFiles.map((f) => ({
                __typename: "DataRoomFile" as const,
                id: f.id,
                name: f.name,
                fileType: f.fileType,
                folderId: f.folderId,
              })),
            },
          },
        };
      },
    );
  }

  function handleDealThreadMessageCreated(
    message: DealThreadMessageCreatedData,
  ) {
    queryClient.invalidateQueries({
      queryKey: ["ActiveDealAccount", { id: activeDealId }],
    });
  }

  function handleDealThreadMessageUpdated(
    message: DealThreadMessageCreatedData,
  ) {
    console.log("message", message);
    dispatch(
      assistantActions.updateDealThreadMessage({
        dealThreadId: message.dealThreadID,
        messageId: message.dealThreadMessageID,
        message: {
          dealThreadID: message.dealThreadID,
          dealThreadMessageID: message.dealThreadMessageID,
          dealThreadMessage: {
            ...message.dealThreadMessage,
            createdAt: getUnixTime(
              new Date(message.dealThreadMessage.createdAt),
            ),
          },
          lastStreamedMessageAt: getUnixTime(new Date()),
        },
      }),
    );
  }

  function handleDataRoomFileError(message: DataRoomFileErrorData) {
    invalidateDealActivities();

    queryClient.invalidateQueries({
      queryKey: ["DataRoomFileError", { id: message.dataRoomFileErrorID }],
    });
    toasts.error(message.message);
  }

  function handleDataRoomFileErrorUpdated(
    message: DataRoomFileErrorUpdatedData,
  ) {
    invalidateDealActivities();

    queryClient.invalidateQueries({
      queryKey: ["DataRoomFileError", { id: message.dataRoomFileErrorID }],
    });
  }

  function handleDataRoomFileErrorDismissed(
    message: DataRoomFileErrorDismissedData,
  ) {
    invalidateDealActivities();

    queryClient.invalidateQueries({
      queryKey: ["DataRoomFileErrors", { dealId: activeDealId }],
    });

    queryClient.invalidateQueries({
      queryKey: ["DataRoomFileError", { id: message.dataRoomFileErrorID }],
    });
  }

  function handleDealExportUpdated(message: DealExportUpdatedData) {
    queryClient.invalidateQueries({
      queryKey: ["DealExports", { dealId: message.dealId }],
    });

    if (message.status === DealExportStatus.Complete) {
      toasts.success("Download created");
      if (message.s3PresignedUrl) {
        const a = document.createElement("a");
        a.href = message.s3PresignedUrl;
        a.target = "_blank";

        document.body.appendChild(a);
        a.click();

        document.body.removeChild(a);
      }
    }
  }

  function handleDataRoomFileCreated(message: DataRoomFileCreatedData) {
    invalidateDealActivities();
    invalidateDataRoomFolders();
    invalidateDataRoomFile(message.dataRoomFileID);
  }

  function handleDataRoomFileUpdated(message: DataRoomFileUpdatedData) {
    invalidateDealActivities();
    invalidateDataRoomFolders();
    invalidateDataRoomFile(message.dataRoomFileID);
  }

  function handleDataRoomFileVersionUpdated(
    message: DataRoomFileVersionCreated,
  ) {
    invalidateDataRoomFolders();
    invalidateDataRoomFile(message.dataRoomFileID);
  }

  function handleDataRoomFileVersionCreated(
    message: DataRoomFileVersionCreated,
  ) {
    invalidateDealActivities();
    invalidateDataRoomFolders();
    invalidateDataRoomFile(message.dataRoomFileID);
  }

  return (
    <WebSocketContext.Provider
      value={{
        sendMessage,
        subscribeToTopic,
        unsubscribeFromTopic,
        lastMessage,
      }}
    >
      {props.children}
    </WebSocketContext.Provider>
  );
}

interface DealFirmGroupPresenceData {
  dealFirmGroup: DealFirmGroupPresence;
}

interface DealExportUpdatedData {
  dealExportId: string;
  dealId: string;
  status: DealExportStatus;
  s3PresignedUrl: string;
}

interface DdqlImportCompleteData {
  dealFirmGroupID: string;
  ddqlID: string;
}

export interface DealThreadMessageCreatedData {
  dealThreadID: string;
  dealThreadMessageID: string;
  dealThreadMessage: DealThreadMessage;
  lastStreamedMessageAt?: number;
}

interface DealThreadMessage {
  id: string;
  status: DealThreadMessageStatus;
  content: DealThreadMessageContent;
  sources?: DealThreadMessageSources;
  role: DealThreadMessageRole;
  createdAt: number;
}

interface DealThreadMessageSources {
  sources: DealThreadMessageSource[];
}

interface DealThreadMessageContent {
  type: string;
  text: {
    value: string;
  };
  blocks?: DealThreadMessageContentBlock[];
  quotes: DealThreadMessageContentQuote[];
  files: DealThreadMessageContentFile[];
}

interface DealThreadMessageContentBlock {
  type: DealThreadMessageContentBlockType;
  durationMs?: number;
  text?: string;
  reasoning?: string;
  toolName?: string;
  toolArgs?: string;
  toolCallId?: string;
  toolResult?: string;
}

export interface DealThreadMessageContentQuote {
  questionId: string;
  quote: string;
  fileId: string;
  dataRoomFileId: string;
  dataRoomFileVersionId: string;
  dataRoomFolderId: string;
  dataRoomFileName: string;
  dataRoomFileType: FileType;
  pageIndex?: number;
  rectsOnPage?: number[][];
  status: DealThreadMessageContentQuoteStatus;
  excel?: SearchDealResultChunkExcel;
}

interface DealThreadMessageContentFile {
  dataRoomFileId: string;
  dataRoomFileName: string;
  dataRoomFileType: FileType;
  dataRoomFileVersionId: string;
  pageIndex?: number;
  rectsOnPage?: number[][];
}

interface DataRoomFileErrorData {
  dataRoomFileErrorID: string;
  dataRoomFileID: string;
  message: string;
}

interface DataRoomFileErrorUpdatedData {
  dataRoomFileErrorID: string;
}

interface DataRoomFileErrorDismissedData {
  dataRoomFileErrorID: string;
  dismissalReason: string;
}

interface DataRoomFileCreatedData {
  dataRoomFileID: string;
}

interface DataRoomFileUpdatedData {
  dataRoomFileID: string;
}

interface DataRoomFileVersionCreated {
  dataRoomFileVersionID: string;
  dataRoomFileID: string;
}

interface BaseWebsocketMessage {
  type: WebsocketMessageType;
  data: object;
}

interface DataRoomFileErrorMessage extends BaseWebsocketMessage {
  type: WebsocketMessageType.DataRoomFileError;
  data: DataRoomFileErrorData;
}

interface DataRoomFileErrorUpdatedMessage extends BaseWebsocketMessage {
  type: WebsocketMessageType.DataRoomFileErrorUpdated;
  data: DataRoomFileErrorUpdatedData;
}

interface DataRoomFileCreatedMessage extends BaseWebsocketMessage {
  type: WebsocketMessageType.DataRoomFileCreated;
  data: DataRoomFileCreatedData;
}

interface DataRoomFileUpdatedMessage extends BaseWebsocketMessage {
  type: WebsocketMessageType.DataRoomFileUpdated;
  data: DataRoomFileUpdatedData;
}

interface DataRoomFileVersionCreatedMessage extends BaseWebsocketMessage {
  type: WebsocketMessageType.DataRoomFileVersionCreated;
  data: DataRoomFileVersionCreated;
}

interface DataRoomFileVersionUpdatedMessage extends BaseWebsocketMessage {
  type: WebsocketMessageType.DataRoomFileVersionUpdated;
  data: DataRoomFileVersionCreated;
}

interface DataRoomFileErrorDismissedMessage extends BaseWebsocketMessage {
  type: WebsocketMessageType.DataRoomFileErrorDismissed;
  data: DataRoomFileErrorDismissedData;
}

interface DealThreadMessageCreatedMessage extends BaseWebsocketMessage {
  type: WebsocketMessageType.DealThreadMessageCreated;
  data: DealThreadMessageCreatedData;
}

interface DealThreadMessageUpdatedMessage extends BaseWebsocketMessage {
  type: WebsocketMessageType.DealThreadMessageUpdated;
  data: DealThreadMessageCreatedData;
}

interface DdqlImportCompleteMessage extends BaseWebsocketMessage {
  type: WebsocketMessageType.DdqlImportComplete;
  data: DdqlImportCompleteData;
}

interface DealFirmGroupPresenceMessage extends BaseWebsocketMessage {
  type: WebsocketMessageType.DealFirmGroupPresence;
  data: DealFirmGroupPresenceData;
}

interface DealExportUpdatedMessage extends BaseWebsocketMessage {
  type: WebsocketMessageType.DealExportUpdated;
  data: DealExportUpdatedData;
}

interface SuggestedAnswerUpdatedData {
  questionID: string;
  suggestedAnswer: {
    id: string;
    status: SuggestedAnswerStatus;
    summary: string;
    blocks: Array<{
      type: SuggestedAnswerBlockType;
      text?: string;
      reasoning?: string;
      durationMs?: number;
      startedAtMs?: number;
      endedAtMs?: number;
      toolName?: string;
      toolArgs?: string;
      toolCallId?: string;
      toolResult?: string;
    }>;
    relevantFiles: Array<{
      id: string;
      name: string;
      fileType: FileType;
      folderId: string;
    }>;
  };
}

interface SuggestedAnswerUpdatedMessage extends BaseWebsocketMessage {
  type: WebsocketMessageType.SuggestedAnswerUpdated;
  data: SuggestedAnswerUpdatedData;
}

export function DataRoomFileTopic(dataRoomFileId: string): string {
  return `data_room_file:${dataRoomFileId}`;
}

export type WebsocketMessage =
  | DataRoomFileErrorMessage
  | DataRoomFileErrorUpdatedMessage
  | DataRoomFileCreatedMessage
  | DataRoomFileErrorDismissedMessage
  | DataRoomFileVersionCreatedMessage
  | DealThreadMessageCreatedMessage
  | DealThreadMessageUpdatedMessage
  | DataRoomFileVersionUpdatedMessage
  | DdqlImportCompleteMessage
  | DealFirmGroupPresenceMessage
  | DealExportUpdatedMessage
  | DataRoomFileUpdatedMessage
  | SuggestedAnswerUpdatedMessage;
